(function() {
  const selectors = {
    form: 'form',
    productForm: 'form[data-product-form]',
    productJson: '[data-product-json]',
    productVariantId: 'input[name="id"]',
    submitControl: 'button[type="submit"], input[type="submit"], button[type="button"]:not([role="combobox"])',
    productFormWrapper: '[data-product-form-wrapper]',
    klaviyoFormMount: '[class*="klaviyo-form-"]',
    klaviyoEmbeddedForm: '[data-testid^="klaviyo-form-"]',
    preloadedFormSource: '[data-klaviyo-waitlist-form-source]',
    quickviewModal: '[data-quickview-modal]',
    waitlistInstance: '[data-klaviyo-waitlist-instance]',
  };

  const SUBMITTED_JOIN_LIST_EVENT_NAME = 'Submitted Join list';
  const SUBMIT_INTENT_WINDOW_MS = 15000;
  const SUBMISSION_DEDUPE_WINDOW_MS = 30000;
  const IDENTIFY_FALLBACK_DELAY_MS = 1500;
  const trackedSubmissionKeys = new Map();
  const activeWaitlistControllers = new Set();
  let klaviyoSubmissionObserverInitialized = false;

  function pruneTrackedSubmissionKeys(now = Date.now()) {
    trackedSubmissionKeys.forEach((timestamp, key) => {
      if (now - timestamp > SUBMISSION_DEDUPE_WINDOW_MS) {
        trackedSubmissionKeys.delete(key);
      }
    });
  }

  function getFirstPresentValue(source, keys) {
    if (!source) {
      return '';
    }

    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }

    return '';
  }

  function runOnce(callback) {
    let hasRun = false;

    return function wrappedCallback(...args) {
      if (hasRun) {
        return;
      }

      hasRun = true;
      callback.apply(this, args);
    };
  }

  function isKlaviyoSubscriptionUrl(url) {
    return typeof url === 'string'
      && url.includes('a.klaviyo.com/client/subscriptions/')
      && url.includes('onsite=true');
  }

  function notifyKlaviyoSubscriptionSuccess(detail) {
    activeWaitlistControllers.forEach((controller) => {
      if (typeof controller.handleKlaviyoSubscriptionSuccess === 'function') {
        controller.handleKlaviyoSubscriptionSuccess(detail);
      }
    });
  }

  function ensureKlaviyoSubmissionObserver() {
    if (klaviyoSubmissionObserverInitialized) {
      return;
    }

    klaviyoSubmissionObserverInitialized = true;

    const originalFetch = window.fetch;
    if (typeof originalFetch === 'function') {
      window.fetch = function patchedKlaviyoFetch(...args) {
        const url = args[0] && args[0].url ? args[0].url : String(args[0]);
        const fetchPromise = originalFetch.apply(this, args);

        if (!isKlaviyoSubscriptionUrl(url)) {
          return fetchPromise;
        }

        return fetchPromise.then((response) => {
          if (response && response.ok) {
            notifyKlaviyoSubscriptionSuccess({
              transport: 'fetch',
              url,
              status: response.status,
            });
          }

          return response;
        });
      };
    }

    const originalXhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function patchedKlaviyoXhrOpen(method, url) {
      this.__klaviyoWaitlistUrl = typeof url === 'string' ? url : String(url);

      if (isKlaviyoSubscriptionUrl(this.__klaviyoWaitlistUrl) && !this.__klaviyoWaitlistBound) {
        this.__klaviyoWaitlistBound = true;
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) {
            notifyKlaviyoSubscriptionSuccess({
              transport: 'xhr',
              url: this.__klaviyoWaitlistUrl,
              status: this.status,
            });
          }
        });
      }

      return originalXhrOpen.apply(this, arguments);
    };
  }

  class KlaviyoWaitlistController extends HTMLElement {
    constructor() {
      super();

      this.handleClick = this.handleClick.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleKlaviyoFormEvent = this.handleKlaviyoFormEvent.bind(this);
      this.handleKlaviyoSubscriptionSuccess = this.handleKlaviyoSubscriptionSuccess.bind(this);
      this.handleObservedSubmissionState = this.handleObservedSubmissionState.bind(this);
      this.syncSubmitLabels = this.syncSubmitLabels.bind(this);
      this.returnPreloadedForm = this.returnPreloadedForm.bind(this);
      this.formWrapper = null;
      this.productForm = null;
      this.productData = null;
      this.isInitialized = false;
      this.lastSubmitIntentAt = 0;
      this.lastSubmitIntentFormId = '';
      this.lastSubmitIntentVariantId = '';
      this.lastResolvedSubmissionState = '';
      this.submissionStateObserver = null;
      this.lastSubmitControlsSignature = '';
      this.lastVariantId = '';
      this.lastSubmittedContactValues = {
        email: '',
        phoneNumber: '',
      };
      this.preloadedFormSource = null;
      this.preloadedFormMount = null;
      this.preloadedFormPlaceholder = null;
      this.preloadedFormObserver = null;
    }

    connectedCallback() {
      if (this.isInitialized) {
        return;
      }

      this.productForm = this.resolveProductForm();
      this.formWrapper = this.closest(selectors.productFormWrapper) || this.productForm?.closest(selectors.productFormWrapper);
      this.productData = this.parseProductData();

      if (!this.productForm) {
        return;
      }

      this.dataset.klaviyoBridgeInitialized = 'true';
      this.isInitialized = true;
      activeWaitlistControllers.add(this);

      this.addEventListener('click', this.handleClick, true);
      this.addEventListener('submit', this.handleSubmit, true);
      window.addEventListener('klaviyoForms', this.handleKlaviyoFormEvent);
      ensureKlaviyoSubmissionObserver();
      this.startSubmissionStateObserver();
      this.activateWaitlistInstanceForCurrentVariant();
      this.mountPreloadedFormForQuickview();
      this.syncSubmitLabels();
      this.lastVariantId = this.currentVariantId;

      if (this.formWrapper) {
        this.formWrapper.addEventListener('theme:variant:change', this.handleVariantChange);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick, true);
      this.removeEventListener('submit', this.handleSubmit, true);
      window.removeEventListener('klaviyoForms', this.handleKlaviyoFormEvent);
      activeWaitlistControllers.delete(this);
      this.stopSubmissionStateObserver();
      this.stopPreloadedFormObserver();
      this.returnPreloadedForm();

      if (this.formWrapper) {
        this.formWrapper.removeEventListener('theme:variant:change', this.handleVariantChange);
      }

      this.clearSubmitIntent();
      this.isInitialized = false;
    }

    resolveProductForm() {
      const productFormId = this.dataset.productFormId;

      if (productFormId) {
        return document.getElementById(productFormId);
      }

      const productFormElement = this.previousElementSibling;
      if (productFormElement && productFormElement.matches('product-form')) {
        return productFormElement.querySelector(selectors.productForm);
      }

      return this.formWrapper ? this.formWrapper.querySelector(selectors.productForm) : null;
    }

    parseProductData() {
      const productJson = this.productForm ? this.productForm.querySelector(selectors.productJson) : null;

      if (!productJson || !productJson.textContent) {
        return null;
      }

      try {
        return JSON.parse(productJson.textContent);
      } catch (error) {
        console.warn('Failed to parse product JSON for Klaviyo waitlist controller', error);
        return null;
      }
    }

    get selectSizeText() {
      return this.getSelectOptionText(this.dataset.selectSizeText || 'Select your size', this.getCtaOptionName());
    }

    get waitlistText() {
      return this.dataset.waitlistText || 'Join the waitlist';
    }

    get variantIdInput() {
      return this.productForm ? this.productForm.querySelector(selectors.productVariantId) : null;
    }

    get optionNames() {
      return Array.isArray(this.productData && this.productData.options) ? this.productData.options : [];
    }

    getCtaOptionIndex() {
      return this.optionNames.length && !this.hasOnlyDefaultVariant() ? 0 : -1;
    }

    getCtaOptionName() {
      if (this.getCtaOptionIndex() < 0) {
        return '';
      }

      const optionName = this.dataset.ctaOptionName || this.optionNames[this.getCtaOptionIndex()] || '';

      return String(optionName).trim();
    }

    getSelectOptionText(baseText, optionName = '') {
      const optionLabel = String(optionName || '').trim();

      if (!optionLabel) {
        return baseText;
      }

      return String(baseText || '').replace(/\bsize\b/gi, (match) => {
        if (match === match.toUpperCase()) {
          return optionLabel.toUpperCase();
        }

        if (match.charAt(0) === match.charAt(0).toUpperCase()) {
          return optionLabel.charAt(0).toUpperCase() + optionLabel.slice(1);
        }

        return optionLabel.toLowerCase();
      });
    }

    get currentVariantId() {
      return this.variantIdInput && this.variantIdInput.value ? this.variantIdInput.value.trim() : '';
    }

    getKlaviyoForms() {
      return Array.from(this.getActiveWaitlistInstance().querySelectorAll(selectors.form));
    }

    get embeddedKlaviyoForm() {
      const activeInstance = this.getActiveWaitlistInstance();
      return activeInstance.querySelector(selectors.klaviyoEmbeddedForm) || this.getKlaviyoForms()[0] || null;
    }

    get embeddedKlaviyoFormId() {
      const activeInstance = this.getActiveWaitlistInstance();
      const embeddedForm = activeInstance.querySelector(selectors.klaviyoEmbeddedForm);
      const embeddedDataTestId = embeddedForm ? embeddedForm.getAttribute('data-testid') : '';

      if (embeddedDataTestId && embeddedDataTestId.startsWith('klaviyo-form-')) {
        return embeddedDataTestId.replace('klaviyo-form-', '').trim();
      }

      const mountNode = activeInstance.querySelector(selectors.klaviyoFormMount);
      if (!mountNode) {
        return '';
      }

      const mountClassName = Array.from(mountNode.classList).find((className) => {
        return className.startsWith('klaviyo-form-') && className !== 'klaviyo-form';
      });

      return mountClassName ? mountClassName.replace('klaviyo-form-', '').trim() : '';
    }

    getWaitlistInstances() {
      return Array.from(this.querySelectorAll(selectors.waitlistInstance));
    }

    getActiveWaitlistInstance() {
      const instances = this.getWaitlistInstances();

      return instances.find((instance) => instance.dataset.klaviyoWaitlistActive === 'true') || instances[0] || this;
    }

    shouldUsePreloadedForm() {
      return Boolean(this.closest(selectors.quickviewModal));
    }

    getPreloadedFormSource() {
      return document.querySelector(selectors.preloadedFormSource);
    }

    getPreloadedSourceMount() {
      const source = this.getPreloadedFormSource();

      return source ? source.querySelector(selectors.klaviyoFormMount) : null;
    }

    hasHydratedKlaviyoForm(mount) {
      return Boolean(mount && (mount.querySelector(selectors.form) || mount.querySelector(selectors.klaviyoEmbeddedForm)));
    }

    observePreloadedFormSource(source) {
      if (!source || this.preloadedFormObserver || typeof MutationObserver !== 'function') {
        return;
      }

      this.preloadedFormObserver = new MutationObserver(() => {
        this.mountPreloadedFormForQuickview();
      });

      this.preloadedFormObserver.observe(source, {
        childList: true,
        subtree: true,
      });
    }

    stopPreloadedFormObserver() {
      if (!this.preloadedFormObserver) {
        return;
      }

      this.preloadedFormObserver.disconnect();
      this.preloadedFormObserver = null;
    }

    mountPreloadedFormForQuickview() {
      if (!this.shouldUsePreloadedForm()) {
        return;
      }

      const currentVariant = this.getCurrentVariant();
      if (!currentVariant || currentVariant.available !== false) {
        this.returnPreloadedForm();
        return;
      }

      const source = this.getPreloadedFormSource();
      const sourceMount = this.preloadedFormMount || this.getPreloadedSourceMount();

      if (!source || !sourceMount || !this.hasHydratedKlaviyoForm(sourceMount)) {
        this.observePreloadedFormSource(source);
        return;
      }

      const activeInstance = this.getActiveWaitlistInstance();
      if (!activeInstance || activeInstance.contains(sourceMount)) {
        this.preloadedFormSource = source;
        this.preloadedFormMount = sourceMount;
        return;
      }

      if (!this.preloadedFormPlaceholder) {
        this.preloadedFormPlaceholder = document.createComment('klaviyo waitlist preload');
      }

      if (sourceMount.parentNode === source) {
        source.insertBefore(this.preloadedFormPlaceholder, sourceMount);
      } else if (!this.preloadedFormPlaceholder.parentNode) {
        source.appendChild(this.preloadedFormPlaceholder);
      }

      const existingMount = activeInstance.querySelector(selectors.klaviyoFormMount);
      if (existingMount && existingMount !== sourceMount) {
        existingMount.replaceWith(sourceMount);
      } else {
        activeInstance.appendChild(sourceMount);
      }

      this.preloadedFormSource = source;
      this.preloadedFormMount = sourceMount;
      this.syncSubmitLabels();
    }

    returnPreloadedForm() {
      if (!this.preloadedFormSource || !this.preloadedFormMount) {
        return;
      }

      if (this.preloadedFormPlaceholder && this.preloadedFormPlaceholder.parentNode) {
        this.preloadedFormPlaceholder.parentNode.insertBefore(this.preloadedFormMount, this.preloadedFormPlaceholder);
        this.preloadedFormPlaceholder.remove();
      } else {
        this.preloadedFormSource.appendChild(this.preloadedFormMount);
      }

      this.preloadedFormMount = null;
    }

    getSubmitControls(scope) {
      const searchScope = scope || this;
      return Array.from(searchScope.querySelectorAll(selectors.submitControl));
    }

    getSubmitControl(form, clickedControl) {
      if (clickedControl) {
        return clickedControl;
      }

      return this.getSubmitControls(form)[0] || null;
    }

    getOptionFieldSelector(optionName) {
      const escapedOptionName = window.CSS && window.CSS.escape ? window.CSS.escape(optionName) : optionName.replace(/"/g, '\\"');
      return `[name="options[${escapedOptionName}]"]`;
    }

    getOptionElements(optionName) {
      const optionFieldSelector = this.getOptionFieldSelector(optionName);
      const optionElements = Array.from((this.formWrapper && this.formWrapper.querySelectorAll(optionFieldSelector)) || []);

      return optionElements.filter((element) => {
        return element.form === this.productForm || element.getAttribute('form') === (this.productForm && this.productForm.id) || element.closest('form') === this.productForm;
      });
    }

    getSelectedOptionValue(optionName) {
      const optionElements = this.getOptionElements(optionName);

      if (!optionElements.length) {
        return '';
      }

      const radioLikeInputs = optionElements.filter((element) => {
        return element instanceof HTMLInputElement && ['radio', 'checkbox'].includes(element.type);
      });

      if (radioLikeInputs.length) {
        const checkedInput = radioLikeInputs.find((input) => input.checked);
        return checkedInput && checkedInput.value ? checkedInput.value.trim() : '';
      }

      const firstElement = optionElements[0];

      if (firstElement instanceof HTMLInputElement || firstElement instanceof HTMLSelectElement || firstElement instanceof HTMLTextAreaElement) {
        return firstElement.value ? firstElement.value.trim() : '';
      }

      return '';
    }

    hasSelectedVariant() {
      if (!this.currentVariantId) {
        return false;
      }

      if (this.hasOnlyDefaultVariant()) {
        return true;
      }

      if (!this.optionNames.length) {
        return true;
      }

      return this.optionNames.every((optionName) => Boolean(this.getSelectedOptionValue(optionName)));
    }

    hasOnlyDefaultVariant() {
      const variants = this.productData && Array.isArray(this.productData.variants) ? this.productData.variants : [];
      const variant = variants[0];

      return variants.length === 1 && this.optionNames.length === 1 && this.optionNames[0] === 'Title' && (!variant || !variant.public_title || variant.title === 'Default Title');
    }

    getCurrentVariant() {
      const variants = this.productData && Array.isArray(this.productData.variants) ? this.productData.variants : [];

      if (!variants.length || !this.currentVariantId) {
        return null;
      }

      return this.getVariantById(this.currentVariantId);
    }

    getVariantById(variantId) {
      const variants = this.productData && Array.isArray(this.productData.variants) ? this.productData.variants : [];

      if (!variants.length || !variantId) {
        return null;
      }

      return variants.find((variant) => String(variant.id) === String(variantId)) || null;
    }

    getSelectedCtaOptionValue() {
      const currentVariant = this.getCurrentVariant();
      const optionIndex = this.getCtaOptionIndex();

      if (optionIndex < 0) {
        return '';
      }

      if (currentVariant && Array.isArray(currentVariant.options)) {
        return currentVariant.options[optionIndex] || '';
      }

      const optionName = this.optionNames[optionIndex];

      return optionName ? this.getSelectedOptionValue(optionName) : '';
    }

    getWaitlistLabel() {
      const optionValue = this.getSelectedCtaOptionValue();
      const optionName = this.getCtaOptionName().toUpperCase();
      const waitlistText = this.waitlistText.toUpperCase();

      return optionValue && optionName ? `${waitlistText} \u2014 ${optionName} ${optionValue}` : waitlistText;
    }

    getEventFormId(detail) {
      return getFirstPresentValue(detail, ['formId', 'formID']) || getFirstPresentValue(detail && detail.metaData, ['$form_id', 'form_id']);
    }

    getEventDetailNodes(detail) {
      const candidateKeys = ['form', 'element', 'el', 'container', 'target'];

      return candidateKeys
        .map((key) => detail && detail[key])
        .filter((value) => value instanceof Element);
    }

    getEmbeddedFormFieldValue(selectorsList) {
      const form = this.embeddedKlaviyoForm;

      if (!form) {
        return '';
      }

      for (const selector of selectorsList) {
        const field = form.querySelector(selector);
        if (field && 'value' in field && String(field.value).trim() !== '') {
          return String(field.value).trim();
        }
      }

      return '';
    }

    getSubmittedContactValues(detail) {
      const metaData = detail && detail.metaData ? detail.metaData : {};
      const email = getFirstPresentValue(metaData, ['$email', 'email'])
        || this.getEmbeddedFormFieldValue(['input[type="email"]', 'input[name="email"]'])
        || this.lastSubmittedContactValues.email;
      const phoneNumber = getFirstPresentValue(metaData, ['$phone_number', 'phone_number', 'phone-number', 'phoneNumber', 'phone'])
        || this.getEmbeddedFormFieldValue(['input[type="tel"]', 'input[name="phone-number"]', 'input[name="phone_number"]'])
        || this.lastSubmittedContactValues.phoneNumber;

      return {
        email,
        phoneNumber,
      };
    }

    startSubmissionStateObserver() {
      if (this.submissionStateObserver || typeof MutationObserver !== 'function') {
        return;
      }

      this.submissionStateObserver = new MutationObserver(() => {
        this.syncSubmitLabelsOnControlChange();
        this.handleObservedSubmissionState();
      });

      this.submissionStateObserver.observe(this, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    stopSubmissionStateObserver() {
      if (!this.submissionStateObserver) {
        return;
      }

      this.submissionStateObserver.disconnect();
      this.submissionStateObserver = null;
      this.lastSubmitControlsSignature = '';
    }

    getSubmissionStatusState() {
      const statusNodes = Array.from(this.querySelectorAll('[role="status"], [aria-live]'));
      const errorKeywords = ['error', 'try again', 'invalid', 'required', 'unable', 'failed', 'problem'];
      const successKeywords = ['thank', "you're", 'you are', 'joined', 'subscribed', 'confirmed', 'success', 'check your'];

      for (const node of statusNodes) {
        const text = node.textContent ? node.textContent.replace(/\s+/g, ' ').trim() : '';

        if (!text) {
          continue;
        }

        const normalizedText = text.toLowerCase();

        if (errorKeywords.some((keyword) => normalizedText.includes(keyword))) {
          return {
            type: 'error',
            text,
          };
        }

        if (successKeywords.some((keyword) => normalizedText.includes(keyword))) {
          return {
            type: 'success',
            text,
          };
        }

        if (!this.embeddedKlaviyoForm) {
          return {
            type: 'success',
            text,
          };
        }
      }

      return null;
    }

    markSubmitIntent() {
      this.lastSubmittedContactValues = this.getSubmittedContactValues();
      this.lastSubmitIntentAt = Date.now();
      this.lastSubmitIntentFormId = this.embeddedKlaviyoFormId;
      this.lastSubmitIntentVariantId = this.currentVariantId;
      this.lastResolvedSubmissionState = '';
    }

    clearSubmitIntent() {
      this.lastSubmitIntentAt = 0;
      this.lastSubmitIntentFormId = '';
      this.lastSubmitIntentVariantId = '';
      this.lastSubmittedContactValues = {
        email: '',
        phoneNumber: '',
      };
    }

    hasRecentSubmitIntent(formId = '') {
      if (!this.lastSubmitIntentAt) {
        return false;
      }

      if (Date.now() - this.lastSubmitIntentAt > SUBMIT_INTENT_WINDOW_MS) {
        this.clearSubmitIntent();
        return false;
      }

      if (formId && this.lastSubmitIntentFormId && formId !== this.lastSubmitIntentFormId) {
        return false;
      }

      return true;
    }

    isEventForThisComponent(detail) {
      const eventNodes = this.getEventDetailNodes(detail);
      if (eventNodes.some((node) => this.contains(node))) {
        return true;
      }

      const eventFormId = this.getEventFormId(detail);
      const embeddedFormId = this.embeddedKlaviyoFormId;

      if (eventFormId && embeddedFormId && eventFormId !== embeddedFormId) {
        return false;
      }

      if (!eventFormId && !embeddedFormId) {
        return false;
      }

      return this.hasRecentSubmitIntent(eventFormId || embeddedFormId);
    }

    getTrackingPayload(detail) {
      const submittedVariantId = getFirstPresentValue(detail, ['variant_id', 'variantId']) || this.lastSubmitIntentVariantId || this.currentVariantId;
      const currentVariant = this.getVariantById(submittedVariantId) || this.getCurrentVariant();
      const productUrl = new URL(window.location.pathname, window.location.origin).toString();
      const variantUrl = currentVariant && currentVariant.id
        ? `${productUrl}?variant=${currentVariant.id}`
        : '';
      const {email, phoneNumber} = this.getSubmittedContactValues(detail);
      const payload = {
        product_id: this.productData ? this.productData.id : '',
        product_title: this.productData ? this.productData.title : '',
        variant_id: currentVariant ? currentVariant.id : submittedVariantId,
        product_url: productUrl,
        variant_url: variantUrl,
      };

      if (email) {
        payload.customer_email = email;
      }

      if (phoneNumber) {
        payload.customer_phone = phoneNumber;
      }

      if (currentVariant) {
        if (currentVariant.title) {
          payload.variant_title = currentVariant.title;
        }

        if (currentVariant.sku) {
          payload.sku = currentVariant.sku;
        }
      }

      return Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== '')
      );
    }

    getIdentityPayload(payload) {
      const identityPayload = {};

      if (payload.customer_email) {
        identityPayload.email = payload.customer_email;
      }

      if (payload.customer_phone) {
        identityPayload.phone_number = payload.customer_phone;
      }

      return identityPayload;
    }

    getSubmissionDedupeKey(payload) {
      const dedupeIdentity = payload.customer_email || payload.customer_phone || 'anonymous';
      const timeBucket = Math.floor(Date.now() / 10000);

      return [
        payload.variant_id || 'unknown-variant',
        dedupeIdentity,
        payload.product_id || 'unknown-product',
        timeBucket,
      ].join('::');
    }

    trackSubmittedForm(detail) {
      const klaviyo = window.klaviyo;

      if (!klaviyo || typeof klaviyo.track !== 'function') {
        return;
      }

      const payload = this.getTrackingPayload(detail);
      const dedupeKey = this.getSubmissionDedupeKey(payload);

      pruneTrackedSubmissionKeys();

      if (trackedSubmissionKeys.has(dedupeKey)) {
        return;
      }

      trackedSubmissionKeys.set(dedupeKey, Date.now());

      const identifyPayload = this.getIdentityPayload(payload);
      const sendTrack = runOnce(() => {
        try {
          klaviyo.track(SUBMITTED_JOIN_LIST_EVENT_NAME, payload);
        } catch (error) {
          trackedSubmissionKeys.delete(dedupeKey);
          console.warn('Failed to track Klaviyo submitted waitlist form event', error);
        }
      });

      if (typeof klaviyo.identify === 'function' && Object.keys(identifyPayload).length) {
        const fallbackTimer = window.setTimeout(sendTrack, IDENTIFY_FALLBACK_DELAY_MS);
        const handleIdentifyComplete = runOnce(() => {
          window.clearTimeout(fallbackTimer);
          sendTrack();
        });

        try {
          const identifyResult = klaviyo.identify(identifyPayload, handleIdentifyComplete);

          if (identifyResult && typeof identifyResult.then === 'function') {
            identifyResult
              .then(handleIdentifyComplete)
              .catch(() => {
                handleIdentifyComplete();
              });
          }
        } catch (error) {
          window.clearTimeout(fallbackTimer);
          console.warn('Failed to identify profile before Klaviyo submitted waitlist form event', error);
          sendTrack();
        }

        return;
      }

      try {
        sendTrack();
      } catch (error) {
        trackedSubmissionKeys.delete(dedupeKey);
        console.warn('Failed to track Klaviyo submitted waitlist form event', error);
      }
    }

    storeOriginalSubmitLabel(control) {
      if (!control) {
        return;
      }

      if (control.tagName === 'INPUT') {
        if (!control.hasAttribute('data-klaviyo-original-value')) {
          control.setAttribute('data-klaviyo-original-value', control.value);
        }

        return;
      }

      if (!control.hasAttribute('data-klaviyo-original-html')) {
        control.setAttribute('data-klaviyo-original-html', control.innerHTML);
      }
    }

    setSubmitLabel(control, label) {
      if (!control) {
        return;
      }

      this.storeOriginalSubmitLabel(control);

      if (control.tagName === 'INPUT') {
        if (control.value === label) {
          return;
        }

        control.value = label;
      } else {
        if (control.innerHTML === label) {
          return;
        }

        control.innerHTML = label;
      }
    }

    restoreSubmitLabel(control) {
      if (!control) {
        return;
      }

      if (control.tagName === 'INPUT') {
        const originalValue = control.getAttribute('data-klaviyo-original-value');

        if (originalValue !== null && control.value !== originalValue) {
          control.value = originalValue;
        }

        control.removeAttribute('data-klaviyo-select-size');
        return;
      }

      const originalHtml = control.getAttribute('data-klaviyo-original-html');

      if (originalHtml !== null && control.innerHTML !== originalHtml) {
        control.innerHTML = originalHtml;
      }

      control.removeAttribute('data-klaviyo-select-size');
    }

    setSelectSizeState(control) {
      this.setSubmitLabel(control, this.selectSizeText);
      if (control) {
        control.setAttribute('data-klaviyo-select-size', 'true');
      }
    }

    setWaitlistState(control) {
      this.setSubmitLabel(control, this.getWaitlistLabel());
      if (control) {
        control.removeAttribute('data-klaviyo-select-size');
      }
    }

    restoreAllSubmitLabels() {
      this.getKlaviyoForms().forEach((form) => {
        this.getSubmitControls(form).forEach((control) => {
          this.restoreSubmitLabel(control);
        });
      });
    }

    getWaitlistInstanceForVariant(variantId) {
      if (!variantId) {
        return null;
      }

      return this.getWaitlistInstances().find((instance) => {
        return instance.dataset.klaviyoWaitlistVariantId === String(variantId);
      }) || null;
    }

    setActiveWaitlistInstance(nextInstance) {
      if (!nextInstance) {
        return;
      }

      this.getWaitlistInstances().forEach((instance) => {
        const isActive = instance === nextInstance;
        if (isActive) {
          instance.dataset.klaviyoWaitlistActive = 'true';
        } else {
          delete instance.dataset.klaviyoWaitlistActive;
        }
        instance.toggleAttribute('aria-hidden', !isActive);
        instance.toggleAttribute('inert', !isActive);
      });

      this.lastResolvedSubmissionState = '';
      this.lastSubmitControlsSignature = '';
      this.clearSubmitIntent();
      this.mountPreloadedFormForQuickview();
    }

    activateWaitlistInstanceForCurrentVariant() {
      const currentVariant = this.getCurrentVariant();

      if (!currentVariant || currentVariant.available !== false) {
        return;
      }

      this.setActiveWaitlistInstance(this.getWaitlistInstanceForVariant(currentVariant.id));
    }

    getSubmitControlsSignature() {
      return this.getKlaviyoForms()
        .map((form, formIndex) => {
          return this.getSubmitControls(form)
            .map((control, controlIndex) => {
              return [
                formIndex,
                controlIndex,
                control.tagName,
                control.type || '',
                control.name || '',
              ].join(':');
            })
            .join('|');
        })
        .join('||');
    }

    syncSubmitLabelsOnControlChange() {
      const submitControlsSignature = this.getSubmitControlsSignature();

      if (!submitControlsSignature || submitControlsSignature === this.lastSubmitControlsSignature) {
        return;
      }

      this.lastSubmitControlsSignature = submitControlsSignature;
      this.syncSubmitLabels();
    }

    syncSubmitLabels() {
      const currentVariant = this.getCurrentVariant();

      this.getKlaviyoForms().forEach((form) => {
        this.getSubmitControls(form).forEach((control) => {
          if (!this.hasSelectedVariant()) {
            this.setSelectSizeState(control);
          } else if (currentVariant && currentVariant.available === false) {
            this.setWaitlistState(control);
          } else {
            this.restoreSubmitLabel(control);
          }
        });
      });
    }

    preventSubmission(event, control) {
      event.preventDefault();
      event.stopPropagation();

      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      this.setSelectSizeState(control || this.getSubmitControl(event.target));
    }

    handleClick(event) {
      const submitControl = event.target.closest(selectors.submitControl);

      if (!submitControl || !this.contains(submitControl)) {
        return;
      }

      if (!this.hasSelectedVariant()) {
        this.preventSubmission(event, submitControl);
        return;
      }

      this.markSubmitIntent();
      this.syncSubmitLabels();
    }

    handleSubmit(event) {
      if (!(event.target instanceof HTMLFormElement) || !this.contains(event.target)) {
        return;
      }

      const submitControl = this.getSubmitControl(event.target, event.submitter || null);

      if (!this.hasSelectedVariant()) {
        this.preventSubmission(event, submitControl);
        return;
      }

      this.markSubmitIntent();
      this.syncSubmitLabels();
    }

    handleVariantChange(event) {
      const nextVariantId = this.currentVariantId;
      const variantChanged = Boolean(nextVariantId && this.lastVariantId && nextVariantId !== this.lastVariantId);

      if (variantChanged) {
        this.activateWaitlistInstanceForCurrentVariant();
      }

      this.lastVariantId = nextVariantId;
      this.mountPreloadedFormForQuickview();
      this.syncSubmitLabels();
    }

    handleKlaviyoFormEvent(event) {
      const detail = event && event.detail ? event.detail : null;

      if (!detail || detail.type !== 'submit') {
        return;
      }

      if (!this.isEventForThisComponent(detail)) {
        return;
      }

      this.trackSubmittedForm(detail);
      this.clearSubmitIntent();
    }

    handleObservedSubmissionState() {
      const trackedFormId = this.lastSubmitIntentFormId || this.embeddedKlaviyoFormId;

      if (!this.hasRecentSubmitIntent(trackedFormId)) {
        return;
      }

      const submissionStatusState = this.getSubmissionStatusState();

      if (!submissionStatusState) {
        return;
      }

      const submissionStateKey = [trackedFormId, submissionStatusState.type, submissionStatusState.text].join('::');

      if (submissionStateKey === this.lastResolvedSubmissionState) {
        return;
      }

      this.lastResolvedSubmissionState = submissionStateKey;

      if (submissionStatusState.type === 'success') {
        this.trackSubmittedForm({
          formId: trackedFormId,
          variant_id: this.lastSubmitIntentVariantId,
          metaData: {
            email: this.lastSubmittedContactValues.email,
            phone_number: this.lastSubmittedContactValues.phoneNumber,
          },
        });
      }

      this.clearSubmitIntent();
    }

    handleKlaviyoSubscriptionSuccess() {
      if (!this.hasRecentSubmitIntent(this.lastSubmitIntentFormId || this.embeddedKlaviyoFormId)) {
        return;
      }

      this.trackSubmittedForm({
        formId: this.lastSubmitIntentFormId || this.embeddedKlaviyoFormId,
        variant_id: this.lastSubmitIntentVariantId,
        metaData: {
          email: this.lastSubmittedContactValues.email,
          phone_number: this.lastSubmittedContactValues.phoneNumber,
        },
      });
      this.clearSubmitIntent();
    }
  }

  if (!customElements.get('klaviyo-waitlist-controller')) {
    customElements.define('klaviyo-waitlist-controller', KlaviyoWaitlistController);
  }
})();
